import { useState, useEffect, useCallback } from 'react';
import { bridgeClient } from '../../../../services/bridgeClient';

const useBridgeStatus = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);

  const checkBridgeStatus = useCallback(async () => {
    try {
      const connected = await bridgeClient.healthCheck();
      setIsConnected(connected);
      if (!connected) {
        setPrinters([]);
        setSelectedPrinter(null);
      }
      return connected;
    } catch (error) {
      setIsConnected(false);
      setPrinters([]);
      setSelectedPrinter(null);
      return false;
    }
  }, []);

  const loadPrinters = useCallback(async () => {
    if (!isConnected) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await bridgeClient.getPrinters();
      const allPrinters = response.printers || [];
      
      // Filter only online and installed printers
      const availablePrinters = allPrinters.filter(printer => 
        printer.status === 'online' || printer.status === 'installed' || printer.status === 'ready'
      );
      
      setPrinters(availablePrinters);
    } catch (error) {
      setError(error.message);
      setPrinters([]);
    } finally {
      setIsLoading(false);
    }
  }, [isConnected]);

  const printLabel = useCallback(async (zplData) => {
    if (!isConnected) {
      throw new Error('Bridge not connected');
    }

    try {
      return await bridgeClient.print(zplData, selectedPrinter);
    } catch (error) {
      throw error;
    }
  }, [isConnected, selectedPrinter]);

  const selectPrinter = useCallback((printer) => {
    setSelectedPrinter(printer);
  }, []);

  const downloadBridgeInstaller = useCallback(async () => {
    try {
      setDownloadProgress({ status: 'fetching', progress: 0 });
      
      // Get the latest release info from GitHub API
      const releaseResponse = await fetch('https://api.github.com/repos/jonathan-am/walasmartwork-bridge-server/releases/latest');
      const releaseData = await releaseResponse.json();
      
      // Find the setup .exe asset (installer, not the executable)
      const exeAsset = releaseData.assets.find(asset => 
        asset.name.endsWith('.exe') && 
        asset.name.toLowerCase().includes('setup')
      );
      
      if (!exeAsset) {
        throw new Error('Instalador não encontrado na última versão');
      }

      setDownloadProgress({ status: 'downloading', progress: 0, fileName: exeAsset.name });

      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = exeAsset.browser_download_url;
      link.download = exeAsset.name;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadProgress({ status: 'completed', progress: 100, fileName: exeAsset.name });
      
      // Clear progress after 3 seconds
      setTimeout(() => {
        setDownloadProgress(null);
      }, 3000);

    } catch (error) {
      console.error('Erro ao baixar o bridge:', error);
      setDownloadProgress({ status: 'error', error: error.message });
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setDownloadProgress(null);
      }, 5000);
    }
  }, []);

  useEffect(() => {
    checkBridgeStatus();
    const interval = setInterval(checkBridgeStatus, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [checkBridgeStatus]);

  useEffect(() => {
    if (isConnected) {
      loadPrinters();
    }
  }, [isConnected, loadPrinters]);

  return {
    isConnected,
    printers,
    selectedPrinter,
    isLoading,
    error,
    downloadProgress,
    checkBridgeStatus,
    loadPrinters,
    printLabel,
    selectPrinter,
    downloadBridgeInstaller,
  };
};

export default useBridgeStatus;
